/**
 * Ce qui BOUGE dans la démo — les séquences jouées dans le temps.
 *
 *   • **La réponse du fan.** On lui écrit, il répond quelques secondes plus
 *     tard, précédé de son « écrit… ». Sans ça un envoi tombe dans le vide et
 *     la vidéo montre une conversation morte.
 *
 *   • **Le paiement.** Sur Dropp le fan clique et c'est réglé ; la démo fait
 *     pareil, quelques secondes après le lien.
 *
 *   • **La vie de fond.** Pendant le shift, des fans ARRIVENT — un « salut »,
 *     une conversation qui démarre de zéro — et d'anciens reviennent. C'est
 *     ce qui remplit « Discussions » sous les yeux du chatteur.
 *
 * 🪦 Le tour de Cyrano (réfléchit → vu → écrit → envoie) vivait ici jusqu'au
 * 07-10-2026. Kayla : « Cyrano, on l'utilise pas, c'est du legacy. » Une
 * démo qui le fait écrire forme à un poste qui n'existe plus.
 *
 * 🪤 Les durées sont choisies pour la CAMÉRA, pas pour la vraisemblance : un
 * fan réel met souvent des minutes à répondre. Ici quelques secondes — assez
 * pour qu'on voie son « écrit… », assez court pour qu'une prise ne traîne pas.
 */
import { enregistrerUneVente } from "./argent";
import { emettre } from "./bus";
import { oublierLesArrivees, unNouveauFan } from "./fabrique";
import { FANS } from "./langues";
import { plusTard } from "./minuteries";
import {
  ajouterUneConversation,
  bulleDuFan,
  leMonde,
  poserLeMessage,
  toutesLesConversations,
  type ConversationDemo,
  type Dit,
  type Geste,
} from "./monde";

function maintenant(): string {
  return new Date().toISOString();
}

function dans(ms: number): string {
  return new Date(Date.now() + ms).toISOString();
}

/* --------------------------------------------------------------------------- */
/* Le fan                                                                        */
/* --------------------------------------------------------------------------- */

/** Ce qui est en vol pour le fan d'une conversation. */
interface ReponseEnVol {
  /** Le geste auquel il est en train de repondre. */
  geste: Geste;
  annuler: () => void;
}

const reponsesEnVol = new Map<string, ReponseEnVol>();

/**
 * Les gestes qui ont le droit de SAUTER des temps : poser une transaction,
 * c'est décider de passer à ce temps-là, et le fan y va.
 */
const GESTES_QUI_SAUTENT: readonly Geste[] = ["menu", "transaction"];

/** Le delai entre deux bulles d'une meme replique. */
const ENTRE_DEUX_BULLES_MS = 2_600;

/**
 * Les conversations où un flow est en train d'écrire : le fan y répond au
 * FLOW (`flows.ts`), pas aux gestes du chatteur.
 */
const flowsEnCours = new Set<string>();

export function marquerLeFlow(conversationId: string, enCours: boolean): void {
  if (enCours) flowsEnCours.add(conversationId);
  else flowsEnCours.delete(conversationId);
}

/**
 * Le fan repond, precede de son « ecrit... ».
 *
 * UN SEUL GESTE, UNE SEULE REPONSE. Deux bulles envoyées coup sur coup ne
 * font répondre le fan qu'une fois : la réponse en vol est repoussée à chaque
 * envoi, et le geste qui compte reste le PREMIER — un texte ne remplace
 * jamais un geste déjà en vol.
 *
 * Ce qu'il dit n'est choisi qu'au moment de la LIVRAISON : une réplique
 * annulée en vol n'est pas consommée.
 */
export function repliqueDuFan(
  conv: ConversationDemo,
  geste: Geste = "texte",
  delaiMs = 6_000,
): void {
  const id = conv.fiche.id;
  if (flowsEnCours.has(id)) return;

  const enVol = reponsesEnVol.get(id);
  let quoi = geste;
  if (enVol) {
    enVol.annuler();
    if (geste === "texte") quoi = enVol.geste;
  }

  const annulerFrappe = plusTard(() => {
    emettre({ type: "typing", conversation_id: id, until: dans(delaiMs * 0.6) });
  }, delaiMs * 0.35);

  const annulerEnvoi = plusTard(() => {
    reponsesEnVol.delete(id);
    dire(conv, choisirLaRepartie(conv, quoi));
  }, delaiMs);

  reponsesEnVol.set(id, {
    geste: quoi,
    annuler: () => {
      annulerFrappe();
      annulerEnvoi();
    },
  });
}

/**
 * Ce que le fan dit MAINTENANT, et ce que ca consomme de la scene.
 *
 * Trois issues, dans cet ordre :
 *   1. le temps en tete attendait ce geste : il est joue, et retire ;
 *   2. un temps PLUS LOIN l'attendait et le geste a le droit de sauter : on y
 *      va, les temps enjambes sont perdus ;
 *   3. sinon il relance, et le temps reste ouvert.
 *
 * Scene epuisee (ou absente) : `repliques`, la file simple.
 */
function choisirLaRepartie(conv: ConversationDemo, geste: Geste): Array<string | Dit> {
  const script = conv.script;
  if (script && script.length > 0) {
    const tete = script[0]!;
    if (tete.sur === geste) {
      script.shift();
      conv.relanceSuivante = 0;
      return tete.dit;
    }
    const plusLoin = GESTES_QUI_SAUTENT.includes(geste)
      ? script.findIndex((t) => t.sur === geste)
      : -1;
    if (plusLoin > 0) {
      const temps = script[plusLoin]!;
      script.splice(0, plusLoin + 1);
      conv.relanceSuivante = 0;
      return temps.dit;
    }
    if (tete.relance && tete.relance.length > 0) {
      const i = (conv.relanceSuivante ?? 0) % tete.relance.length;
      conv.relanceSuivante = i + 1;
      return [tete.relance[i]!];
    }
  }
  const suivante = conv.repliques.shift();
  return suivante === undefined ? [] : [suivante];
}

/**
 * Pose ce que le fan dit — une bulle par entree, separees par un « ecrit... ».
 * Un fan qui pose sa question en deux temps se lit comme quelqu'un de vrai.
 */
export function dire(conv: ConversationDemo, bulles: Array<string | Dit>): void {
  const id = conv.fiche.id;
  bulles.forEach((texte, i) => {
    const poser = () => {
      const msg = poserLeMessage(conv, bulleDuFan(conv, texte, maintenant()));
      emettre({ type: "new_message", conversation_id: id, message: msg });
    };
    if (i === 0) {
      poser();
      return;
    }
    plusTard(() => {
      emettre({ type: "typing", conversation_id: id, until: dans(1_200) });
    }, ENTRE_DEUX_BULLES_MS * i - 1_400);
    plusTard(poser, ENTRE_DEUX_BULLES_MS * i);
  });
}

/**
 * Le fan PAIE — la seule chose que la demo doive produire elle-meme, parce
 * qu'aucun geste du cockpit ne la declenche.
 *
 * Le credit tombe AVANT sa replique, pas apres : c'est le `new_message` du fan
 * qui fait refetcher la liste, donc un revenu pose apres lui n'apparaitrait
 * qu'au refetch suivant, et le compteur de la barre resterait fige a l'image.
 */
export function paiementDuFan(
  conv: ConversationDemo,
  cents: number,
  delaiMs = 5_000,
): void {
  plusTard(() => {
    conv.fiche.revenue_cents += cents;
    conv.fiche.has_active_order = false;
    conv.fiche.offre_en_attente = null;
    conv.payeLe = maintenant();
    if (conv.fiche.stage === "new" || conv.fiche.stage.startsWith("fan")) {
      conv.fiche.stage = "customer_low";
    }
    // 🎉 La vente se fête et monte au classement, comme une vraie.
    enregistrerUneVente({
      cents,
      devise: "EUR",
      fan: conv.fiche.fan_name || null,
      creatrice: conv.fiche.creator_display_name || null,
      produit: null,
    });
    emettre({ type: "conversation_state_changed", conversation_id: conv.fiche.id });
  }, delaiMs);
}

/* ────────────────────────────────────────────────────────────────────────── */
/* La vie de fond                                                             */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Pendant le shift, le répertoire VIT : un nouveau fan arrive à peu près une
 * fois par minute, et de temps en temps un ancien revient. Ce qui frappe
 * quand on ouvre le vrai poste, c'est que « Discussions » se remplit tout
 * seul — une démo figée ne montre pas le métier.
 *
 * ⛔ Les scènes en sont EXCLUES : ce sont elles qu'on filme, et elles restent
 * dans l'état où on les a posées jusqu'à ce qu'on les touche.
 */
const PREMIERE_ARRIVEE_MS = 20_000;

let ambianceArmee = false;

export function lancerLaVieDeFond(): void {
  if (ambianceArmee) return;
  ambianceArmee = true;
  programmerUnPassage(PREMIERE_ARRIVEE_MS);
}

function programmerUnPassage(delai: number, premier = true): void {
  plusTard(() => {
    const monde = leMonde();
    if (monde) {
      // Le premier passage est toujours une arrivée — c'est elle qu'on veut
      // voir peu après « Démarrer mon shift ». Ensuite, deux fois sur trois
      // un NOUVEAU fan, sinon un ancien qui revient.
      if (premier || Math.random() < 2 / 3) arriveUnFan(monde.creatrices);
      else unAncienRevient();
    }
    programmerUnPassage(40_000 + Math.floor(Math.random() * 30_000), false);
  }, delai);
}

function arriveUnFan(creatrices: NonNullable<ReturnType<typeof leMonde>>["creatrices"]): void {
  const { conv, bulles } = unNouveauFan(creatrices);
  ajouterUneConversation(conv);
  dire(conv, bulles);
}

function unAncienRevient(): void {
  const fond = toutesLesConversations().filter(
    (c) => !c.scene && !flowsEnCours.has(c.fiche.id) && c.messages.at(-1)?.direction === "out",
  );
  const conv = fond[Math.floor(Math.random() * fond.length)];
  if (!conv) return;
  const retours = FANS[conv.langue].retours;
  dire(conv, [retours[Math.floor(Math.random() * retours.length)]!]);
}

/** Remet la vie de fond à zéro quand le monde est reconstruit. */
export function oublierLaVieDeFond(): void {
  ambianceArmee = false;
  flowsEnCours.clear();
  reponsesEnVol.clear();
  oublierLesArrivees();
}
